import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import Seo from "../components/Seo";
import {
  ArrowRight,
  CheckCircle,
  Factory,
  Warehouse,
  Truck,
  Wrench,
  Eye,
  ShoppingCart,
  ListChecks,
  Database,
  CalendarBlank,
  Storefront,
  ChartLineUp,
} from "@phosphor-icons/react";

const COPY = {
  cs: {
    eyebrow: "Pilíř 06 / POS",
    title: "POS materiály — od výroby po reporting.",
    sub:
      "POS materiály jsou investice. Stojany, displeje, regálové systémy, shelf talkery, wobblery, paletové brandingy — každý prvek musí být ve správný čas na daném místě, v bezchybném stavu a správně instalovaný. Jinak peníze vynaložené na výrobu a kampaň nepřinesou návratnost.",
    leadIn:
      "Víme, jak fungují řetězce, jaká mají pravidla a jak realizovat kampaň přesně tak, jak ji klient naplánoval. Pracujeme pro všechny obchodní řetězce i nezávislý trh v České republice a na Slovensku.",
    headlineMetric: { k: "5 000+", v: "paletových míst ve vlastním skladu" },
    tabs: [
      { id: "a", num: "A", label: "POS Fulfillment" },
      { id: "b", num: "B", label: "POS E-shop" },
      { id: "c", num: "C", label: "Promokalendar" },
    ],
    a: {
      eyebrow: "A · POS Fulfillment",
      title: "Od výroby po instalaci. Jedno číslo, jeden e-mail, jeden partner.",
      lead: "Bez výmluv, bez přehazování odpovědnosti.",
      blocks: [
        {
          icon: "Factory",
          tag: "Výroba",
          h: "Výroba POS materiálů.",
          body: [
            "Spolupracujeme s ověřenými výrobci POS materiálů a zajišťujeme výrobu celého spektra instore komunikačních prvků — od jednoduchých shelf talkerů a wobblerů přes podlahové displeje a paletové ostrovy až po komplexní brandované stojany na míru.",
            "Každý projekt začíná briefingem vašich požadavků. Navrhujeme řešení, která fungují v konkrétním retailovém prostředí — s ohledem na dostupný prostor, pravidla řetězce a cíl komunikace. Výsledkem jsou materiály, které jsou nejen vizuálně silné, ale i prakticky realizovatelné v terénu.",
          ],
        },
        {
          icon: "Warehouse",
          tag: "Sklad & logistika",
          h: "Skladování a logistika.",
          body: [
            "Skladová infrastruktura je základ spolehlivé POS distribuce. Global Marketing využívá ke skladování externí sklad s kapacitou větší než 5 000 paletových míst. Součástí skladovacích a logistických služeb je i kompletace a plnění stojanů před distribucí.",
            "Distribuce probíhá prostřednictvím ověřených přepravních partnerů. Pro speciální závozy a instalace s příjezdem přesně ve sjednaný čas disponujeme VIP logistickým týmem s více než 20 lety zkušeností.",
          ],
        },
        {
          icon: "Truck",
          tag: "Distribuce",
          h: "Dostaneme POS materiály tam, kde je potřebujete. Včas.",
          body: [
            "POS materiály doručujeme po celé České republice a Slovensku na všechna určená obchodní místa. Váš obchodní zástupce nebo merchandiser nemusí řešit logistiku — materiál ho čeká na místě.",
            "Systém závozu lze nastavit podle vašich potřeb — standardní doručení, express závozy pro urgentní kampaně nebo pravidelné zásobování vybraných prodejen. Veškerou koordinaci přebíráme my.",
          ],
        },
        {
          icon: "Wrench",
          tag: "Instalace",
          h: "Stojan stojí tam, kde má stát. A vypadá tak, jak má vypadat.",
          body: [
            "Instalace POS materiálů a stojanů v prodejnách je disciplína, která vyžaduje zkušenosti, koordinaci s vedením prodejny a znalost pravidel jednotlivých řetězců. Naše instalační týmy mají za sebou stovky instalací napříč českým i slovenským retailem — od supermarketů a hypermarketů přes DIY a čerpací stanice až po specializované prodejny.",
            "Každá instalace je zdokumentovaná fotodokumentací s GPS lokalizací přímo z mobilní aplikace MERVIK. Výsledek vidíte v reportingovém systému SYMES — ještě tentýž den.",
          ],
        },
        {
          icon: "Eye",
          tag: "Monitoring",
          h: "Víte, zda váš POS materiál skutečně funguje?",
          body: [
            "Instalace není konec příběhu. Pravidelný monitoring POS materiálů zajišťuje, že stojany a komunikační prvky zůstávají v požadovaném stavu po celou dobu kampaně — a že případné problémy jsou řešeny okamžitě.",
            "Sledujeme přítomnost a stav vašich POS materiálů na každé servisované prodejně, správnost umístění a soulad s aktuální kampaní, poškození nebo zastaralé materiály vyžadující výměnu. Zároveň monitorujeme POS komunikaci konkurence ve vaší kategorii.",
            "Všechna data jsou dostupná v QlikView dashboardu — filtrovaná podle řetězce, regionu, prodejny nebo časového období.",
          ],
        },
      ],
    },
    b: {
      eyebrow: "B · POS E-shop",
      title: "Váš obchodní zástupce si POS materiál objedná sám. Za 2 minuty, z mobilu, kdekoliv.",
      lead:
        "POS E-shop je vlastní softwarové řešení Global Marketing vyvinuté přímo pro obchodní týmy výrobců a dodavatelů. Intuitivní systém, který každému obchodnímu zástupci dává přesný přehled nad dostupností POS materiálů a plnou kontrolu nad jejich objednáváním, zásobováním a doručením — přímo na prodejnu, do skladu nebo na domácí adresu.",
      tagline: "Méně administrativy. Méně chyb. Méně zmeškaných kampaní. Více času na prodej.",
      problem: {
        eyebrow: "Problém",
        h: "Obchodní zástupce se stará o prodej. Ne o logistiku POS materiálů.",
        body: [
          "V mnoha firmách funguje zásobování obchodních týmů POS materiály stále stejně — e-mailem, telefonem nebo přes sdílený Excel. Výsledkem jsou zbytečné prodlevy, chybějící materiály v klíčový moment kampaně, duplicitní objednávky a nulový přehled o tom, co je na skladě a co ne.",
          "POS E-shop tento problém řeší jednou provždy. Každý obchodní zástupce objednává přímo, vidí aktuální skladovou dostupnost a zvolí si přesně, kdy a kam materiál doručit.",
        ],
      },
      steps: {
        eyebrow: "Objednávka v třech krocích",
        h: "Výběr POS, určení závozového místa, potvrzení. To je vše.",
        items: [
          { n: "01", h: "Výběr POS", t: "Obchodní zástupce vybere zboží a přidá do košíku — z aktuálního katalogu se skladovou dostupností." },
          { n: "02", h: "Závozové místo", t: "Tři varianty: individuální adresa, přednastavené externí sklady nebo konkrétní prodejna z databáze řetězců. Express závoz pro urgenci." },
          { n: "03", h: "Potvrzení", t: "Objednávka prochází validací dle nastavených odpovědných osob. Hotovo." },
        ],
      },
      admin: {
        eyebrow: "Správa & administrace",
        h: "Kompletní přehled nad celým POS flow — pro vedení i pro sklad.",
        body:
          "Administrátorské rozhraní poskytuje přehled všech objednávek, statistik prodeje a stavu skladu v reálném čase. Vedení firmy vidí výstupy filtrované podle obchodního zástupce, týmu, produktu, kategorie nebo časového období. Vše exportovatelné do CSV.",
        bullets: [
          "Modul expirace produktu — hlídá záruční doby a posílá automatické notifikace.",
          "Max Buy — omezuje maximální počet zakoupených kusů v daném časovém úseku pro jednotlivce.",
          "CSV export všech statistik a objednávek.",
        ],
      },
      logistics: {
        eyebrow: "Propojení se sklady a logistikou",
        h: "Objednávka v e-shopu. Doručení na místo. V čase, který si obchodní zástupce vybral.",
        body: [
          "POS E-shop je plně integrovaný do logistického ekosystému Global Marketing. Po odeslání objednávky přebírá štafetu logistický tým a síť přepravních partnerů. Závozy probíhají po celé České republice a Slovensku s přesností na hodiny.",
          "Systém lze propojit s logistickým centrem pro řízení závozových míst, dopravy a skladových zásob a s informačním systémem klienta pro fakturaci, správu uživatelů a skladu. Obě propojení jsou řešena individuálně dle požadavků klienta.",
        ],
      },
      benefits: {
        eyebrow: "Přínosy pro klienta",
        items: [
          { h: "Méně administrativy", t: "Obchodní zástupci tráví méně času administrativou a více času prodejem." },
          { h: "Real-time přehled", t: "Vedení má kompletní přehled nad čerpáním POS materiálů v reálném čase." },
          { h: "Sklad ví", t: "Sklad přesně ví, co, kdy a kam má doručit." },
          { h: "Žádné prostoje", t: "Kampaně nestojí kvůli chybějícím materiálům." },
        ],
      },
    },
    c: {
      eyebrow: "C · Promokalendar",
      title: "Jeden systém. Celý retail. Všechny promoční příležitosti na jednom místě.",
      lead:
        "Promokalendar je webová platforma z dílny IT oddělení Global Marketing, která mění způsob, jakým retaileři prodávají a koordinují své promoční a marketingové příležitosti — a jakým je dodavatelé nakupují. Konec e-mailů, tabulek a telefonátů. Začátek transparentního, efektivního a digitálního trhu promoční prezentace.",
      forWho: {
        eyebrow: "Pro koho",
        h: "Promokalendar slouží oběma stranám retailového trhu.",
        items: [
          {
            label: "Retailerům",
            t: "Přehledná správa a prodej všech dostupných marketingových ploch a aktivit, které nabízejí svým dodavatelům. Kompletní přehled obsazenosti, kapacity a výnosů z promočních aktivit v jednom systému.",
          },
          {
            label: "Dodavatelům",
            t: "Snadný přístup k celé nabídce promočních příležitostí retailera, transparentní rezervace termínů a plánování kampaní bez zbytečné administrativy.",
          },
        ],
      },
      problem: {
        eyebrow: "Proč to potřebujete",
        h: "Promoční příležitosti v retailu se dnes často prodávají tak, jak se prodávaly před dvaceti lety.",
        body: [
          "Retailer má celou řadu marketingových ploch a aktivit — letáky, čela regálů, paletová umístění, digitální displeje, ochutnávky, věrnostní akce, produkty týdne. Každá z těchto příležitostí má svůj termín, svoji cenu, svoji kapacitu a svá pravidla. A přesto se celý tento obchod z velké části odehrává v e-mailech, Excelu a telefonátech.",
          "Výsledkem mohou být promarněné příležitosti, zdvojené rezervace, nepřehledné podmínky a zbytečné administrativní náklady na obou stranách. Promokalendar tento chaos nahrazuje přehledným, digitálním a transparentním systémem.",
        ],
      },
      today: {
        eyebrow: "Co Promokalendar umí dnes",
        h: "Komplexní správa a prodej promočních příležitostí retailera.",
        body:
          "Promokalendar umožňuje retailerovi vytvořit přehledný katalog všech dostupných marketingových ploch a aktivit — s popisem, podmínkami, cenami a dostupnými termíny. Dodavatel si sám prochází nabídku, vybírá termíny a rezervuje příležitosti, které odpovídají jeho kampaňové strategii.",
        bullets: [
          "Přehled obsazenosti a volné kapacity v reálném čase",
          "Správa rezervací a schvalovací procesy",
          "Promoční kalendář pro obě strany",
          "Evidence smluvních podmínek a cenových nabídek",
        ],
      },
      future: {
        eyebrow: "Co přinese nová verze · NEW",
        h: "Full-service promoční platforma. Přímo od retailera, pod jeho značkou.",
        body:
          "Připravovaný update Promokalendar posune celou platformu na zcela novou úroveň. Retailer nebude jen prodávat promoční příležitosti — bude moci dodavatelům pod svou vlastní značkou nabídnout kompletní full-service zajištění celé promoční aktivity. Od výroby přes instalaci až po monitoring. A zároveň získává nový zdroj příjmů z výroby a instalace, které dosud směřovaly k externím dodavatelům.",
        twoCol: [
          {
            h: "Retailer získává",
            items: [
              "Přehledný digitální systém pro správu a prodej všech promočních příležitostí",
              "Nový zdroj příjmů z výroby a instalace POS materiálů",
              "Silnější pozici v obchodních vztazích s dodavateli",
              "Přehled o plánovaných kampaních a jejich realizaci v reálném čase",
              "Vlastní brandovanou platformu posilující image inovativního partnera",
            ],
          },
          {
            h: "Dodavatel získává",
            items: [
              "Transparentní přístup ke všem promočním příležitostem retailera",
              "Plánování kampaní s předstihem a rezervace bez administrativní zátěže",
              "Možnost objednat výrobu a instalaci přímo přes retailera",
              "Žádné hledání výrobců a koordinace s montážní firmou",
              "Celý proces — rezervace, výroba, instalace, potvrzení — v jednom systému",
            ],
          },
        ],
        outro: "Jeden systém, jeden partner, jedna smlouva.",
      },
      tech: {
        eyebrow: "Technologie",
        h: "Webová aplikace, žádná instalace, individuální branding.",
        body:
          "Promokalendar je webová aplikace přístupná z jakéhokoliv zařízení bez nutnosti instalace. Systém je navržen tak, aby byl intuitivní pro obě strany — retailera i dodavatele — a aby minimalizoval čas strávený administrativou. Platforma je škálovatelná — od jednoho formátu prodejny až po celou síť se stovkami dodavatelů. Každý retailer dostane vlastní brandované prostředí odpovídající jeho corporate identity.",
      },
      pilot: {
        eyebrow: "Pilot · v aktivním vývoji",
        h: "Chcete být mezi prvními, kdo Promokalendar vyzkouší?",
        body:
          "Platforma je v aktivním vývoji a připravujeme první pilotní spuštění. Pokud jste retailer, který chce digitalizovat prodej svých promočních příležitostí, nebo dodavatel, který chce mít přehled nad celým promočním trhem — ozvěte se. Rádi vám ukážeme, jak Promokalendar funguje a co nová verze přinese.",
      },
    },
    finalCta: {
      eyebrow: "Pojďme si o tom promluvit",
      title: "Chcete pokrýt vaši POS kampaň od výroby po reporting?",
      sub: "Napište nám a domluvíme si konkrétní postup pro vaši značku, kategorii a kampaňový plán.",
    },
  },
  en: {
    eyebrow: "Pillar 06 / POS",
    title: "POS materials — from production to reporting.",
    sub:
      "POS materials are an investment. Stands, displays, shelf systems, talkers, wobblers, pallet brandings — every element must be in the right place at the right time, in flawless condition and correctly installed. Otherwise the money spent on production and the campaign won't return.",
    leadIn:
      "We know how the chains work, what their rules are, and how to execute a campaign exactly as the client planned. We work with all retail chains and the independent market in the Czech Republic and Slovakia.",
    headlineMetric: { k: "5,000+", v: "pallet slots in our warehouse" },
    tabs: [
      { id: "a", num: "A", label: "POS Fulfillment" },
      { id: "b", num: "B", label: "POS E-shop" },
      { id: "c", num: "C", label: "Promokalendar" },
    ],
    a: {
      eyebrow: "A · POS Fulfillment",
      title: "From production to installation. One number, one e-mail, one partner.",
      lead: "No excuses, no responsibility ping-pong.",
      blocks: [
        {
          icon: "Factory",
          tag: "Production",
          h: "POS production.",
          body: [
            "We work with verified POS manufacturers and deliver the full spectrum of in-store communication elements — from simple shelf talkers and wobblers, through floor displays and pallet islands, to complex custom branded stands.",
            "Every project starts with a brief. We design solutions that work in the specific retail environment — taking available space, chain rules and the communication goal into account. The result: materials that are not only visually strong but also practically deliverable in the field.",
          ],
        },
        {
          icon: "Warehouse",
          tag: "Warehouse & logistics",
          h: "Storage and logistics.",
          body: [
            "Warehouse infrastructure is the foundation of reliable POS distribution. Global Marketing uses an external warehouse with capacity over 5,000 pallet slots. Storage and logistics services include assembly and stand kitting before distribution.",
            "Distribution runs through verified transport partners. For special drops and installations with exact-time arrival we operate a VIP logistics team with 20+ years of experience.",
          ],
        },
        {
          icon: "Truck",
          tag: "Distribution",
          h: "We deliver POS where you need it. On time.",
          body: [
            "POS materials are delivered across the Czech Republic and Slovakia to all designated points of sale. Your sales rep or merchandiser doesn't have to deal with logistics — the material is waiting for them on site.",
            "The drop-off scheme can be set up to your needs — standard delivery, express drops for urgent campaigns or regular replenishment of selected stores. We take over the full coordination.",
          ],
        },
        {
          icon: "Wrench",
          tag: "Installation",
          h: "The stand stands where it should — and looks how it should.",
          body: [
            "Installing POS materials and stands in stores is a discipline that requires experience, coordination with store management and knowledge of each chain's rules. Our installation teams have hundreds of installations behind them across Czech and Slovak retail — from supermarkets and hypermarkets through DIY and petrol stations to specialty stores.",
            "Every installation is documented with photo evidence and GPS coordinates straight from the MERVIK mobile app. You see the result in the SYMES reporting system — the same day.",
          ],
        },
        {
          icon: "Eye",
          tag: "Monitoring",
          h: "Do you know if your POS is actually working?",
          body: [
            "Installation is not the end of the story. Regular POS monitoring ensures stands and communication elements stay in the required condition for the whole campaign — and that any issues are resolved immediately.",
            "We track presence and condition of your POS in every serviced store, placement correctness and alignment with the active campaign, damaged or outdated materials needing replacement. We also monitor competitor POS communication in your category.",
            "All data is available in a QlikView dashboard — filtered by chain, region, store or period.",
          ],
        },
      ],
    },
    b: {
      eyebrow: "B · POS E-shop",
      title: "Your sales rep orders POS themselves. In 2 minutes, from a phone, anywhere.",
      lead:
        "POS E-shop is a Global Marketing software solution built directly for the sales teams of manufacturers and suppliers. An intuitive system that gives every sales rep precise visibility into POS availability and full control over ordering, replenishment and delivery — straight to the store, warehouse or home address.",
      tagline: "Less admin. Fewer mistakes. No missed campaigns. More time selling.",
      problem: {
        eyebrow: "Problem",
        h: "A sales rep handles selling. Not POS logistics.",
        body: [
          "In many companies, supplying field teams with POS still works the same way — by e-mail, phone or a shared Excel. The result: needless delays, missing materials at the campaign moment, duplicate orders and zero visibility into stock.",
          "POS E-shop solves this once and for all. Every rep orders directly, sees current stock and picks exactly when and where the material should be delivered.",
        ],
      },
      steps: {
        eyebrow: "Order in three steps",
        h: "Pick POS, set the drop-off, confirm. That's it.",
        items: [
          { n: "01", h: "Pick POS", t: "The rep picks items into a cart — from a live catalogue with stock visibility." },
          { n: "02", h: "Drop-off", t: "Three options: individual address, pre-set external warehouses, or a specific store from the chain database. Express drop available." },
          { n: "03", h: "Confirm", t: "The order goes through validation by configured approvers. Done." },
        ],
      },
      admin: {
        eyebrow: "Admin & control",
        h: "Full visibility over the whole POS flow — for management and warehouse.",
        body:
          "The admin interface gives a real-time overview of all orders, sales statistics and stock. Management sees outputs filtered by rep, team, product, category or period. Everything exportable to CSV.",
        bullets: [
          "Expiry module — tracks shelf life and pushes automatic notifications.",
          "Max Buy — limits the number of items per person within a defined window.",
          "CSV export of all statistics and orders.",
        ],
      },
      logistics: {
        eyebrow: "Warehouse & logistics integration",
        h: "Order in the e-shop. Delivered on site. At the time the rep chose.",
        body: [
          "POS E-shop is fully integrated into the Global Marketing logistics ecosystem. Once an order is sent, the logistics team and our transport partner network take over. Drops run across the Czech Republic and Slovakia with hour-level accuracy.",
          "The system can integrate with a logistics centre for managing drop-off points, transport and stock — and with the client's information system for invoicing, user and warehouse management. Both integrations are tailored to client requirements.",
        ],
      },
      benefits: {
        eyebrow: "Benefits",
        items: [
          { h: "Less admin", t: "Reps spend less time on admin and more time selling." },
          { h: "Real-time view", t: "Management has complete visibility over POS consumption in real time." },
          { h: "Warehouse knows", t: "Warehouse knows exactly what, when and where to deliver." },
          { h: "No downtime", t: "Campaigns don't stall because of missing materials." },
        ],
      },
    },
    c: {
      eyebrow: "C · Promokalendar",
      title: "One system. The whole retail. All promo opportunities in one place.",
      lead:
        "Promokalendar is a web platform built by Global Marketing's IT department, transforming how retailers sell and coordinate their promo and marketing opportunities — and how suppliers buy them. End of e-mails, spreadsheets and phone calls. Start of a transparent, efficient and digital marketplace for promo presentation.",
      forWho: {
        eyebrow: "Who it's for",
        h: "Promokalendar serves both sides of the retail market.",
        items: [
          {
            label: "Retailers",
            t: "Clear management and sale of all marketing slots and activities offered to suppliers. Full visibility on occupancy, capacity and revenue from promo activities in one system.",
          },
          {
            label: "Suppliers",
            t: "Easy access to the retailer's full promo offer, transparent slot reservations and campaign planning without unnecessary admin.",
          },
        ],
      },
      problem: {
        eyebrow: "Why you need it",
        h: "Promo opportunities in retail are still often sold the way they were sold twenty years ago.",
        body: [
          "A retailer has many marketing slots — leaflets, endcaps, pallet placements, digital displays, tastings, loyalty deals, products of the week. Every opportunity has its date, price, capacity and rules. Yet most of this trade still happens in e-mails, Excel and phone calls.",
          "The result: missed opportunities, duplicate reservations, opaque terms and unnecessary admin cost on both sides. Promokalendar replaces that chaos with a clear, digital and transparent system.",
        ],
      },
      today: {
        eyebrow: "What Promokalendar does today",
        h: "Comprehensive management and sale of the retailer's promo opportunities.",
        body:
          "Promokalendar lets the retailer build a clear catalogue of all available marketing slots — with description, terms, price and available dates. The supplier browses the offer, picks dates and reserves opportunities matching their campaign strategy.",
        bullets: [
          "Real-time occupancy and free capacity",
          "Reservation management and approval workflows",
          "Promo calendar for both sides",
          "Records of contractual terms and price offers",
        ],
      },
      future: {
        eyebrow: "What the new version brings · NEW",
        h: "Full-service promo platform. Direct from the retailer, under their brand.",
        body:
          "The upcoming Promokalendar update lifts the platform to a new level. The retailer won't just sell promo slots — they'll be able to offer suppliers, under their own brand, complete full-service execution of the entire promo activity. From production through installation to monitoring. And gain a new revenue stream from production and installation that used to flow to external suppliers.",
        twoCol: [
          {
            h: "Retailer gains",
            items: [
              "A clear digital system for selling all promo opportunities",
              "A new revenue stream from POS production and installation",
              "A stronger position in supplier relationships",
              "Real-time visibility on planned campaigns and execution",
              "A branded platform reinforcing innovator image",
            ],
          },
          {
            h: "Supplier gains",
            items: [
              "Transparent access to the retailer's full promo offer",
              "Long-lead campaign planning and reservations without admin",
              "Ability to order production and installation directly via the retailer",
              "No vendor hunting, no installation coordination",
              "The whole process — booking, production, installation, confirmation — in one system",
            ],
          },
        ],
        outro: "One system, one partner, one contract.",
      },
      tech: {
        eyebrow: "Technology",
        h: "Web app, no install, individual branding.",
        body:
          "Promokalendar is a web application accessible from any device without installation. The system is designed to be intuitive for both sides — retailer and supplier — and to minimise time spent on admin. The platform scales — from a single store format to entire networks with hundreds of suppliers. Every retailer gets a branded environment matching their corporate identity.",
      },
      pilot: {
        eyebrow: "Pilot · in active development",
        h: "Want to be among the first to try Promokalendar?",
        body:
          "The platform is in active development and we're preparing the first pilot launches. If you're a retailer who wants to digitise the sale of promo opportunities, or a supplier who wants visibility over the whole promo market — get in touch. We'll show you how Promokalendar works and what the new version will bring.",
      },
    },
    finalCta: {
      eyebrow: "Let's talk",
      title: "Want to cover your POS campaign from production to reporting?",
      sub: "Drop us a line and we'll align a concrete plan for your brand, category and campaign schedule.",
    },
  },
  sk: {
    eyebrow: "Pilier 06 / POS",
    title: "POS materiály — od výroby po reporting.",
    sub:
      "POS materiály sú investícia. Stojany, displeje, regálové systémy, shelf talkery, wobblery, paletové brandingy — každý prvok musí byť v správny čas na danom mieste, v bezchybnom stave a správne inštalovaný. Inak peniaze vynaložené na výrobu a kampaň neprinesú návratnosť.",
    leadIn:
      "Vieme, ako fungujú reťazce, aké majú pravidlá a ako realizovať kampaň presne tak, ako ju klient naplánoval. Pracujeme pre všetky obchodné reťazce aj nezávislý trh v Českej republike a na Slovensku.",
    headlineMetric: { k: "5 000+", v: "paletových miest vo vlastnom sklade" },
    tabs: [
      { id: "a", num: "A", label: "POS Fulfillment" },
      { id: "b", num: "B", label: "POS E-shop" },
      { id: "c", num: "C", label: "Promokalendar" },
    ],
    a: {
      eyebrow: "A · POS Fulfillment",
      title: "Od výroby po inštaláciu. Jedno číslo, jeden e-mail, jeden partner.",
      lead: "Bez výhovoriek, bez prehadzovania zodpovednosti.",
      blocks: [
        {
          icon: "Factory",
          tag: "Výroba",
          h: "Výroba POS materiálov.",
          body: [
            "Spolupracujeme s overenými výrobcami POS materiálov a zaisťujeme výrobu celého spektra instore komunikačných prvkov — od jednoduchých shelf talkerov a wobblerov cez podlahové displeje a paletové ostrovy až po komplexné brandované stojany na mieru.",
            "Každý projekt začína briefingom vašich požiadaviek. Navrhujeme riešenia, ktoré fungujú v konkrétnom retailovom prostredí — s ohľadom na dostupný priestor, pravidlá reťazca a cieľ komunikácie. Výsledkom sú materiály, ktoré sú nielen vizuálne silné, ale aj prakticky realizovateľné v teréne.",
          ],
        },
        {
          icon: "Warehouse",
          tag: "Sklad & logistika",
          h: "Skladovanie a logistika.",
          body: [
            "Skladová infraštruktúra je základ spoľahlivej POS distribúcie. Global Marketing využíva na skladovanie externý sklad s kapacitou vyše 5 000 paletových miest. Súčasťou skladovacích a logistických služieb je aj kompletizácia a plnenie stojanov pred distribúciou.",
            "Distribúcia prebieha prostredníctvom overených prepravných partnerov. Pre špeciálne závozy a inštalácie s príchodom presne v dohodnutý čas disponujeme VIP logistickým tímom s viac ako 20 rokmi skúseností.",
          ],
        },
        {
          icon: "Truck",
          tag: "Distribúcia",
          h: "Dostaneme POS materiály tam, kde ich potrebujete. Včas.",
          body: [
            "POS materiály doručujeme po celej Českej republike a Slovensku na všetky určené obchodné miesta. Váš obchodný zástupca alebo merchandiser nemusí riešiť logistiku — materiál ho čaká na mieste.",
            "Systém závozu možno nastaviť podľa vašich potrieb — štandardné doručenie, express závozy pre urgentné kampane alebo pravidelné zásobovanie vybraných predajní. Celú koordináciu preberáme my.",
          ],
        },
        {
          icon: "Wrench",
          tag: "Inštalácia",
          h: "Stojan stojí tam, kde má stáť. A vyzerá tak, ako má vyzerať.",
          body: [
            "Inštalácia POS materiálov a stojanov v predajniach je disciplína, ktorá vyžaduje skúsenosti, koordináciu s vedením predajne a znalosť pravidiel jednotlivých reťazcov. Naše inštalačné tímy majú za sebou stovky inštalácií naprieč českým aj slovenským retailom — od supermarketov a hypermarketov cez DIY a čerpacie stanice až po špecializované predajne.",
            "Každá inštalácia je zdokumentovaná fotodokumentáciou s GPS lokalizáciou priamo z mobilnej aplikácie MERVIK. Výsledok vidíte v reportingovom systéme SYMES — ešte v ten istý deň.",
          ],
        },
        {
          icon: "Eye",
          tag: "Monitoring",
          h: "Viete, či váš POS materiál skutočne funguje?",
          body: [
            "Inštalácia nie je koniec príbehu. Pravidelný monitoring POS materiálov zaisťuje, že stojany a komunikačné prvky zostávajú v požadovanom stave počas celej kampane — a že prípadné problémy sú riešené okamžite.",
            "Sledujeme prítomnosť a stav vašich POS materiálov v každej servisovanej predajni, správnosť umiestnenia a súlad s aktuálnou kampaňou, poškodené alebo zastarané materiály vyžadujúce výmenu. Zároveň monitorujeme POS komunikáciu konkurencie vo vašej kategórii.",
            "Všetky dáta sú dostupné v QlikView dashboarde — filtrované podľa reťazca, regiónu, predajne alebo časového obdobia.",
          ],
        },
      ],
    },
    b: {
      eyebrow: "B · POS E-shop",
      title: "Váš obchodný zástupca si POS materiál objedná sám. Za 2 minúty, z mobilu, kdekoľvek.",
      lead:
        "POS E-shop je vlastné softvérové riešenie Global Marketing vyvinuté priamo pre obchodné tímy výrobcov a dodávateľov. Intuitívny systém, ktorý každému obchodnému zástupcovi dáva presný prehľad nad dostupnosťou POS materiálov a plnú kontrolu nad ich objednávaním, zásobovaním a doručením — priamo na predajňu, do skladu alebo na domácu adresu.",
      tagline: "Menej administratívy. Menej chýb. Menej zmeškaných kampaní. Viac času na predaj.",
      problem: {
        eyebrow: "Problém",
        h: "Obchodný zástupca sa stará o predaj. Nie o logistiku POS materiálov.",
        body: [
          "V mnohých firmách funguje zásobovanie obchodných tímov POS materiálmi stále rovnako — e-mailom, telefónom alebo cez zdieľaný Excel. Výsledkom sú zbytočné meškania, chýbajúce materiály v kľúčový moment kampane, duplicitné objednávky a nulový prehľad o tom, čo je na sklade a čo nie.",
          "POS E-shop tento problém rieši raz a navždy. Každý obchodný zástupca objednáva priamo, vidí aktuálnu skladovú dostupnosť a zvolí si presne, kedy a kam materiál doručiť.",
        ],
      },
      steps: {
        eyebrow: "Objednávka v troch krokoch",
        h: "Výber POS, určenie závozového miesta, potvrdenie. To je všetko.",
        items: [
          { n: "01", h: "Výber POS", t: "Obchodný zástupca vyberie tovar a pridá do košíka — z aktuálneho katalógu so skladovou dostupnosťou." },
          { n: "02", h: "Závozové miesto", t: "Tri varianty: individuálna adresa, prednastavené externé sklady alebo konkrétna predajňa z databázy reťazcov. Express závoz pre urgenciu." },
          { n: "03", h: "Potvrdenie", t: "Objednávka prechádza validáciou podľa nastavených zodpovedných osôb. Hotovo." },
        ],
      },
      admin: {
        eyebrow: "Správa & administrácia",
        h: "Kompletný prehľad nad celým POS flow — pre vedenie aj pre sklad.",
        body:
          "Administrátorské rozhranie poskytuje prehľad všetkých objednávok, štatistík predaja a stavu skladu v reálnom čase. Vedenie firmy vidí výstupy filtrované podľa obchodného zástupcu, tímu, produktu, kategórie alebo časového obdobia. Všetko exportovateľné do CSV.",
        bullets: [
          "Modul expirácie produktu — stráži záručné doby a posiela automatické notifikácie.",
          "Max Buy — obmedzuje maximálny počet zakúpených kusov v danom časovom úseku pre jednotlivca.",
          "CSV export všetkých štatistík a objednávok.",
        ],
      },
      logistics: {
        eyebrow: "Prepojenie so skladmi a logistikou",
        h: "Objednávka v e-shope. Doručenie na miesto. V čase, ktorý si obchodný zástupca vybral.",
        body: [
          "POS E-shop je plne integrovaný do logistického ekosystému Global Marketing. Po odoslaní objednávky preberá štafetu logistický tím a sieť prepravných partnerov. Závozy prebiehajú po celej Českej republike a Slovensku s presnosťou na hodiny.",
          "Systém je možné prepojiť s logistickým centrom na riadenie závozových miest, dopravy a skladových zásob a s informačným systémom klienta pre fakturáciu, správu používateľov a skladu. Obe prepojenia sú riešené individuálne podľa požiadaviek klienta.",
        ],
      },
      benefits: {
        eyebrow: "Prínosy pre klienta",
        items: [
          { h: "Menej administratívy", t: "Obchodní zástupcovia trávia menej času administratívou a viac času predajom." },
          { h: "Real-time prehľad", t: "Vedenie má kompletný prehľad nad čerpaním POS materiálov v reálnom čase." },
          { h: "Sklad vie", t: "Sklad presne vie, čo, kedy a kam má doručiť." },
          { h: "Žiadne prestoje", t: "Kampane nestoja kvôli chýbajúcim materiálom." },
        ],
      },
    },
    c: {
      eyebrow: "C · Promokalendar",
      title: "Jeden systém. Celý retail. Všetky promočné príležitosti na jednom mieste.",
      lead:
        "Promokalendar je webová platforma z dielne IT oddelenia Global Marketing, ktorá mení spôsob, akým retaileri predávajú a koordinujú svoje promočné a marketingové príležitosti — a akým ich dodávatelia nakupujú. Koniec e-mailov, tabuliek a telefonátov. Začiatok transparentného, efektívneho a digitálneho trhu promočnej prezentácie.",
      forWho: {
        eyebrow: "Pre koho",
        h: "Promokalendar slúži obom stranám retailového trhu.",
        items: [
          {
            label: "Retailerom",
            t: "Prehľadná správa a predaj všetkých dostupných marketingových plôch a aktivít, ktoré ponúkajú svojim dodávateľom. Kompletný prehľad obsadenosti, kapacity a výnosov z promočných aktivít v jednom systéme.",
          },
          {
            label: "Dodávateľom",
            t: "Ľahký prístup k celej ponuke promočných príležitostí retailera, transparentná rezervácia termínov a plánovanie kampaní bez zbytočnej administratívy.",
          },
        ],
      },
      problem: {
        eyebrow: "Prečo to potrebujete",
        h: "Promočné príležitosti v retaile sa dnes často predávajú tak, ako sa predávali pred dvadsiatimi rokmi.",
        body: [
          "Retailer má celý rad marketingových plôch a aktivít — letáky, čelá regálov, paletové umiestnenia, digitálne displeje, ochutnávky, vernostné akcie, produkty týždňa. Každá z týchto príležitostí má svoj termín, svoju cenu, svoju kapacitu a svoje pravidlá. A predsa sa celý tento obchod z veľkej časti odohráva v e-mailoch, Exceli a telefonátoch.",
          "Výsledkom môžu byť premárnené príležitosti, zdvojené rezervácie, neprehľadné podmienky a zbytočné administratívne náklady na oboch stranách. Promokalendar tento chaos nahrádza prehľadným, digitálnym a transparentným systémom.",
        ],
      },
      today: {
        eyebrow: "Čo Promokalendar dnes dokáže",
        h: "Komplexná správa a predaj promočných príležitostí retailera.",
        body:
          "Promokalendar umožňuje retailerovi vytvoriť prehľadný katalóg všetkých dostupných marketingových plôch a aktivít — s popisom, podmienkami, cenami a dostupnými termínmi. Dodávateľ si sám prechádza ponuku, vyberá termíny a rezervuje príležitosti, ktoré zodpovedajú jeho kampaňovej stratégii.",
        bullets: [
          "Prehľad obsadenosti a voľnej kapacity v reálnom čase",
          "Správa rezervácií a schvaľovacie procesy",
          "Promočný kalendár pre obe strany",
          "Evidencia zmluvných podmienok a cenových ponúk",
        ],
      },
      future: {
        eyebrow: "Čo prinesie nová verzia · NEW",
        h: "Full-service promočná platforma. Priamo od retailera, pod jeho značkou.",
        body:
          "Pripravovaný update Promokalendar posunie celú platformu na úplne novú úroveň. Retailer nebude len predávať promočné príležitosti — bude môcť dodávateľom pod svojou vlastnou značkou ponúknuť kompletné full-service zaistenie celej promočnej aktivity. Od výroby cez inštaláciu až po monitoring. A zároveň získava nový zdroj príjmov z výroby a inštalácie, ktoré doteraz smerovali k externým dodávateľom.",
        twoCol: [
          {
            h: "Retailer získava",
            items: [
              "Prehľadný digitálny systém na správu a predaj všetkých promočných príležitostí",
              "Nový zdroj príjmov z výroby a inštalácie POS materiálov",
              "Silnejšiu pozíciu v obchodných vzťahoch s dodávateľmi",
              "Prehľad o plánovaných kampaniach a ich realizácii v reálnom čase",
              "Vlastnú brandovanú platformu posilňujúcu image inovatívneho partnera",
            ],
          },
          {
            h: "Dodávateľ získava",
            items: [
              "Transparentný prístup ku všetkým promočným príležitostiam retailera",
              "Plánovanie kampaní s predstihom a rezervácie bez administratívnej záťaže",
              "Možnosť objednať výrobu a inštaláciu priamo cez retailera",
              "Žiadne hľadanie výrobcov a koordinácia s montážnou firmou",
              "Celý proces — rezervácia, výroba, inštalácia, potvrdenie — v jednom systéme",
            ],
          },
        ],
        outro: "Jeden systém, jeden partner, jedna zmluva.",
      },
      tech: {
        eyebrow: "Technológia",
        h: "Webová aplikácia, žiadna inštalácia, individuálny branding.",
        body:
          "Promokalendar je webová aplikácia prístupná z akéhokoľvek zariadenia bez nutnosti inštalácie. Systém je navrhnutý tak, aby bol intuitívny pre obe strany — retailera aj dodávateľa — a aby minimalizoval čas strávený administratívou. Platforma je škálovateľná — od jedného formátu predajne až po celú sieť so stovkami dodávateľov. Každý retailer dostane vlastné brandované prostredie zodpovedajúce jeho corporate identity.",
      },
      pilot: {
        eyebrow: "Pilot · v aktívnom vývoji",
        h: "Chcete byť medzi prvými, kto Promokalendar vyskúša?",
        body:
          "Platforma je v aktívnom vývoji a pripravujeme prvé pilotné spustenie. Pokiaľ ste retailer, ktorý chce digitalizovať predaj svojich promočných príležitostí, alebo dodávateľ, ktorý chce mať prehľad nad celým promočným trhom — ozvite sa. Radi vám ukážeme, ako Promokalendar funguje a čo nová verzia prinesie.",
      },
    },
    finalCta: {
      eyebrow: "Poďme sa o tom porozprávať",
      title: "Chcete pokryť vašu POS kampaň od výroby po reporting?",
      sub: "Napíšte nám a dohodneme si konkrétny postup pre vašu značku, kategóriu a kampaňový plán.",
    },
  },
};

const ICONS = { Factory, Warehouse, Truck, Wrench, Eye };

const Eyebrow = ({ children, accent, light }) => (
  <span className={`gm-eyebrow ${accent ? "text-[#0A4DFF]" : light ? "text-white/50" : ""}`}>{children}</span>
);

const Hero = ({ c }) => (
  <section className="relative bg-white border-b border-[#e5e5e5]" data-testid="pos-hero">
    <div className="absolute inset-0 gm-grid-bg pointer-events-none" />
    <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-14 md:pb-20 relative">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-9">
          <Eyebrow accent>{c.eyebrow}</Eyebrow>
          <h1 className="font-display text-5xl md:text-7xl tracking-tighter font-medium leading-[0.95] mt-6 max-w-4xl">
            {c.title}
          </h1>
          <p className="text-lg md:text-xl text-[#525252] mt-8 max-w-3xl leading-relaxed">{c.sub}</p>
          <p className="text-base text-[#0a0a0a] mt-6 max-w-3xl leading-relaxed border-l-2 border-[#0A4DFF] pl-5">
            {c.leadIn}
          </p>
        </div>
        <div className="lg:col-span-3 flex lg:items-end">
          <div className="bg-[#0a0a0a] text-white p-6 w-full">
            <div className="font-mono text-[10px] tracking-widest text-white/50">// CAPACITY</div>
            <div className="font-display text-5xl md:text-6xl tracking-tighter mt-3 leading-none">{c.headlineMetric.k}</div>
            <div className="text-sm text-white/70 mt-3 leading-relaxed">{c.headlineMetric.v}</div>
          </div>
        </div>
      </div>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 border-t border-[#e5e5e5]">
        {c.tabs.map((tb, i) => (
          <a
            key={tb.id}
            href={`#${tb.id}`}
            className="group border-b md:border-b-0 md:border-r border-[#e5e5e5] last:border-r-0 py-6 pr-6 flex items-baseline gap-4 hover:bg-[#fafafa] transition-colors"
            data-testid={`pos-tab-${tb.id}`}
          >
            <span className="font-display text-4xl md:text-5xl tracking-tighter text-[#0A4DFF]">{tb.num}</span>
            <span>
              <span className="block font-mono text-[10px] tracking-widest text-[#9a9a9a]">SECTION</span>
              <span className="block font-display text-lg md:text-xl mt-1 group-hover:text-[#0A4DFF] transition-colors">{tb.label}</span>
            </span>
          </a>
        ))}
      </div>
    </div>
  </section>
);

const SectionDivider = ({ tab, label, scrollId }) => (
  <section id={scrollId} className="bg-[#0A4DFF] text-white scroll-mt-24" data-testid={`pos-divider-${scrollId}`}>
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 md:py-10 flex items-baseline gap-6">
      <span className="font-display text-5xl md:text-7xl tracking-tighter">{tab}</span>
      <span className="font-mono text-xs tracking-widest uppercase opacity-80">{label}</span>
    </div>
  </section>
);

const SectionA = ({ c }) => (
  <>
    <section className="py-14 md:py-20 bg-white" data-testid="pos-a-intro">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-stretch">
          <div className="md:col-span-7">
            <Eyebrow accent>{c.eyebrow}</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05] font-medium">
              {c.title}
            </h2>
            <p className="text-lg text-[#525252] mt-5 italic">{c.lead}</p>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 border-t border-[#0a0a0a] pt-6">
              {[
                { k: "5 000+", v: "paletových míst ve vlastním skladu" },
                { k: "Turn-key", v: "od briefu po instalaci v prodejně" },
                { k: "CZ + SK", v: "celosíťové pokrytí výroby a distribuce" },
                { k: "Reporting", v: "fotodokumentace každé instalace" },
              ].map((m, i) => (
                <li key={i} className="flex items-baseline gap-3 py-1">
                  <span className="font-display text-2xl tracking-tight text-[#0A4DFF] min-w-[90px]">{m.k}</span>
                  <span className="text-sm text-[#525252] leading-tight">{m.v}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-5">
            <figure
              className="border border-[#e5e5e5] bg-[#0a0a0a] overflow-hidden h-full min-h-[420px]"
              data-testid="pos-a-warehouse-photo"
            >
              <img
                src="/pos/warehouse.jpg?v=3"
                alt="Vlastní sklad Global Marketing — symetrická ulička s plně zaplněnými paletovými regály"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </figure>
          </div>
        </div>
      </div>
    </section>

    {c.blocks.map((b, i) => {
      const Icon = ICONS[b.icon];
      const reverse = i % 2 === 1;
      return (
        <section
          key={i}
          className={`py-12 md:py-16 ${i % 2 === 0 ? "bg-[#fafafa]" : "bg-white border-y border-[#e5e5e5]"}`}
          data-testid={`pos-a-block-${i}`}
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className={`grid grid-cols-1 md:grid-cols-12 gap-8 items-start ${reverse ? "md:[&>*:first-child]:order-2" : ""}`}>
              <div className="md:col-span-5">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-[#0A4DFF] text-white">
                  {Icon ? <Icon size={24} weight="thin" /> : null}
                </div>
                <span className="block gm-eyebrow mt-5">{b.tag}</span>
                <h3 className="font-display text-3xl md:text-4xl tracking-tight mt-3 leading-[1.05]">{b.h}</h3>
              </div>
              <div className="md:col-span-7 space-y-4">
                {b.body.map((p, j) => (
                  <p key={j} className="text-[#0a0a0a] leading-relaxed">{p}</p>
                ))}
              </div>
            </div>
          </div>
        </section>
      );
    })}
  </>
);

const SectionB = ({ c }) => (
  <>
    <section className="py-14 md:py-20 bg-white" data-testid="pos-b-intro">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Eyebrow accent>{c.eyebrow}</Eyebrow>
        <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05] font-medium max-w-4xl">
          {c.title}
        </h2>
        <p className="text-lg text-[#525252] mt-6 max-w-3xl leading-relaxed">{c.lead}</p>
        <p className="text-base text-[#0a0a0a] mt-6 max-w-3xl leading-relaxed border-l-2 border-[#0A4DFF] pl-5 italic">
          {c.tagline}
        </p>
      </div>
    </section>

    <section className="py-14 md:py-20 bg-[#fafafa]" data-testid="pos-b-problem">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-8">
        <div className="md:col-span-5">
          <Eyebrow>{c.problem.eyebrow}</Eyebrow>
          <h3 className="font-display text-2xl md:text-4xl tracking-tight mt-4 leading-[1.1]">{c.problem.h}</h3>
        </div>
        <div className="md:col-span-7 space-y-4">
          {c.problem.body.map((p, i) => (
            <p key={i} className="text-[#0a0a0a] leading-relaxed">{p}</p>
          ))}
        </div>
      </div>
    </section>

    <section className="py-14 md:py-20 bg-[#0a0a0a] text-white" data-testid="pos-b-steps">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <span className="font-mono uppercase tracking-[0.22em] text-[13px] font-medium text-[#5B82FF]">{c.steps.eyebrow}</span>
        <h3 className="font-display text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05] max-w-4xl">{c.steps.h}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 mt-12 border border-white/10">
          {c.steps.items.map((it, i) => (
            <div key={i} className="p-7 md:p-9 border-b md:border-b-0 last:border-b-0 border-white/10 md:border-r last:md:border-r-0">
              <div className="font-display text-5xl md:text-6xl tracking-tighter text-white/40">{it.n}</div>
              <h4 className="font-display text-2xl mt-4 leading-tight">{it.h}</h4>
              <p className="text-sm text-white/70 mt-3 leading-relaxed">{it.t}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="py-14 md:py-20 bg-white border-y border-[#e5e5e5]" data-testid="pos-b-admin">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-8">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3">
            <Database size={22} weight="thin" className="text-[#0A4DFF]" />
            <Eyebrow>{c.admin.eyebrow}</Eyebrow>
          </div>
          <h3 className="font-display text-2xl md:text-4xl tracking-tight mt-4 leading-[1.1]">{c.admin.h}</h3>
        </div>
        <div className="md:col-span-7">
          <p className="text-[#0a0a0a] leading-relaxed">{c.admin.body}</p>
          <ul className="mt-6 border-t border-[#0a0a0a] divide-y divide-[#e5e5e5]">
            {c.admin.bullets.map((b, i) => (
              <li key={i} className="py-3 flex items-start gap-3 text-sm leading-relaxed">
                <CheckCircle size={16} weight="thin" className="text-[#0A4DFF] mt-0.5 shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>

    <section className="py-14 md:py-20 bg-[#fafafa]" data-testid="pos-b-logistics">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-stretch">
          {/* LEFT — small photo */}
          <div className="md:col-span-5">
            <figure
              className="border border-[#e5e5e5] bg-[#0a0a0a] overflow-hidden h-full"
              data-testid="pos-b-scanner-photo"
            >
              <img
                src="/pos/scanner.jpg"
                alt="Skenování čárového kódu zásilky — sledování pohybu zboží napříč skladem a logistikou"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </figure>
          </div>

          {/* RIGHT — eyebrow, title, body, feature row */}
          <div className="md:col-span-7">
            <div className="flex items-center gap-3">
              <Truck size={22} weight="thin" className="text-[#0A4DFF]" />
              <Eyebrow>{c.logistics.eyebrow}</Eyebrow>
            </div>
            <h3 className="font-display text-2xl md:text-4xl tracking-tight mt-4 leading-[1.1]">
              {c.logistics.h}
            </h3>
            <div className="space-y-4 mt-6">
              {c.logistics.body.map((p, i) => (
                <p key={i} className="text-[#0a0a0a] leading-relaxed">{p}</p>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-8 border-t border-[#0a0a0a] pt-6">
              {[
                { k: "CZ + SK", v: "celosíťové závozy" },
                { k: "Real-time", v: "tracking zásilek" },
                { k: "API", v: "napojení na klienta" },
              ].map((m, i) => (
                <div key={i} className="flex items-baseline gap-3">
                  <span className="font-display text-xl tracking-tight text-[#0A4DFF]">{m.k}</span>
                  <span className="text-sm text-[#525252] leading-tight">{m.v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>

    <section className="py-14 md:py-20 bg-white" data-testid="pos-b-benefits">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Eyebrow accent>{c.benefits.eyebrow}</Eyebrow>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
          {c.benefits.items.map((it, i) => (
            <div key={i} className="border-t-2 border-[#0a0a0a] pt-5">
              <span className="font-mono text-[10px] tracking-widest text-[#525252]">{`B.0${i + 1}`}</span>
              <h4 className="font-display text-xl md:text-2xl mt-3 tracking-tight leading-tight">{it.h}</h4>
              <p className="text-sm text-[#525252] mt-3 leading-relaxed">{it.t}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  </>
);

const SectionC = ({ c }) => (
  <>
    <section className="py-14 md:py-20 bg-white" data-testid="pos-c-intro">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Eyebrow accent>{c.eyebrow}</Eyebrow>
        <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05] font-medium max-w-4xl">
          {c.title}
        </h2>
        <p className="text-lg text-[#525252] mt-6 max-w-3xl leading-relaxed">{c.lead}</p>
      </div>
    </section>

    <section className="py-14 md:py-20 bg-[#fafafa]" data-testid="pos-c-forwho">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Eyebrow>{c.forWho.eyebrow}</Eyebrow>
        <h3 className="font-display text-2xl md:text-4xl tracking-tight mt-4 leading-[1.1] max-w-3xl">{c.forWho.h}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10">
          {c.forWho.items.map((it, i) => (
            <div key={i} className="bg-white border border-[#e5e5e5] p-7">
              <div className="flex items-center gap-3">
                {i === 0 ? <Storefront size={22} weight="thin" className="text-[#0A4DFF]" /> : <Database size={22} weight="thin" className="text-[#0A4DFF]" />}
                <span className="gm-eyebrow text-[#0A4DFF]">{it.label}</span>
              </div>
              <p className="text-[#0a0a0a] mt-4 leading-relaxed">{it.t}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="py-14 md:py-20 bg-white border-y border-[#e5e5e5]" data-testid="pos-c-problem">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-8">
        <div className="md:col-span-5">
          <Eyebrow>{c.problem.eyebrow}</Eyebrow>
          <h3 className="font-display text-2xl md:text-4xl tracking-tight mt-4 leading-[1.1]">{c.problem.h}</h3>
        </div>
        <div className="md:col-span-7 space-y-4">
          {c.problem.body.map((p, i) => (
            <p key={i} className="text-[#0a0a0a] leading-relaxed">{p}</p>
          ))}
        </div>
      </div>
    </section>

    <section className="py-14 md:py-20 bg-[#fafafa]" data-testid="pos-c-today">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-8">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3">
            <CalendarBlank size={22} weight="thin" className="text-[#0A4DFF]" />
            <Eyebrow>{c.today.eyebrow}</Eyebrow>
          </div>
          <h3 className="font-display text-2xl md:text-4xl tracking-tight mt-4 leading-[1.1]">{c.today.h}</h3>
        </div>
        <div className="md:col-span-7">
          <p className="text-[#0a0a0a] leading-relaxed">{c.today.body}</p>
          <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6">
            {c.today.bullets.map((b, i) => (
              <li key={i} className="py-3 flex items-start gap-3 text-sm leading-relaxed border-b border-[#e5e5e5]">
                <CheckCircle size={16} weight="thin" className="text-[#0A4DFF] mt-0.5 shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>

    <section className="py-14 md:py-20 bg-[#0a0a0a] text-white" data-testid="pos-c-future">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <span className="font-mono uppercase tracking-[0.22em] text-[13px] font-medium text-[#5B82FF]">{c.future.eyebrow}</span>
        <h3 className="font-display text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05] max-w-4xl font-medium">{c.future.h}</h3>
        <p className="text-white/70 mt-6 max-w-3xl leading-relaxed">{c.future.body}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10">
          {c.future.twoCol.map((col, i) => (
            <div key={i} className={`p-7 md:p-9 ${i === 0 ? "bg-[#0A4DFF]" : "bg-white text-[#0a0a0a] border border-[#0A4DFF]"}`}>
              <h4 className="font-display text-2xl md:text-3xl leading-tight">{col.h}</h4>
              <ul className={`mt-6 space-y-3 ${i === 0 ? "" : ""}`}>
                {col.items.map((it, j) => (
                  <li key={j} className={`flex items-start gap-3 text-sm leading-relaxed border-t pt-3 first:border-t-0 first:pt-0 ${i === 0 ? "border-white/15" : "border-[#e5e5e5]"}`}>
                    <CheckCircle size={16} weight="thin" className={`shrink-0 mt-0.5 ${i === 0 ? "" : "text-[#0A4DFF]"}`} />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="font-display text-2xl md:text-3xl mt-10 max-w-3xl border-l-2 border-[#5B82FF] pl-5 leading-tight">
          {c.future.outro}
        </p>
      </div>
    </section>

    <section className="py-14 md:py-20 bg-white" data-testid="pos-c-tech">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-8">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3">
            <ChartLineUp size={22} weight="thin" className="text-[#0A4DFF]" />
            <Eyebrow>{c.tech.eyebrow}</Eyebrow>
          </div>
          <h3 className="font-display text-2xl md:text-4xl tracking-tight mt-4 leading-[1.1]">{c.tech.h}</h3>
        </div>
        <p className="md:col-span-7 text-[#0a0a0a] leading-relaxed">{c.tech.body}</p>
      </div>
    </section>
  </>
);

const FinalCta = ({ c }) => {
  const { t } = useLang();
  return (
    <section className="py-14 md:py-20 bg-white border-t border-[#e5e5e5]" data-testid="pos-final-cta">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-4xl">
          <Eyebrow accent>{c.eyebrow}</Eyebrow>
          <h2 className="font-display text-4xl md:text-6xl tracking-tighter mt-5 leading-[0.95] font-medium">{c.title}</h2>
          <p className="text-[#525252] mt-5 leading-relaxed max-w-2xl">{c.sub}</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/kontakt" className="gm-btn gm-btn-primary" data-testid="pos-cta-kontakt">
              {t.nav.contact} <ArrowRight size={16} />
            </Link>
            <a href="mailto:info@globalmarketing.cz" className="gm-btn gm-btn-outline">
              info@globalmarketing.cz
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default function Pos() {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.cs;
  return (
    <div data-testid="pos-page">
      <Seo
        title={{ cs: "POS · Fulfillment, E-shop, Promokalendar", en: "POS · Fulfillment, E-shop, Promokalendar" }}
        description={{
          cs:
            "POS materiály od výroby po reporting. Vlastní sklad 5 000+ palet, POS E-shop pro obchodní týmy a Promokalendar pro retailery.",
          en:
            "POS materials from production to reporting. 5,000+ pallet warehouse, POS E-shop for sales teams and Promokalendar for retailers.",
        }}
        path="/pos"
      />
      <Hero c={c} />
      <SectionDivider tab="A" label={c.tabs[0].label} scrollId="a" />
      <SectionA c={c.a} />
      <SectionDivider tab="B" label={c.tabs[1].label} scrollId="b" />
      <SectionB c={c.b} />
      <SectionDivider tab="C" label={c.tabs[2].label} scrollId="c" />
      <SectionC c={c.c} />
      <FinalCta c={c.finalCta} />
    </div>
  );
}
